import React from "react";

import * as styles from "./styles/SceneDialog.module.css";

/**
 * SceneDialog is a non-blocking overlay card that floats above scene content.
 *
 * Intentionally NOT built on Modal.jsx / react-modal2: that component provides
 * focus trapping, backdrop, portal rendering, and esc-to-close — all of which
 * assume a blocking overlay. Inheriting that infrastructure would mean opting
 * out of almost everything it does, and focus trapping in particular would be
 * wrong here.
 *
 * a11y: role="region" + aria-labelledby/aria-label on the card. role="region"
 * is used (not role="dialog") because the card does not trap focus or block
 * interaction — it is a labelled landmark inside the scene.
 *
 * Positioning: the wrapper defaults to centered (position: absolute, top/left
 * 50%) inside its nearest positioned ancestor. Override placement via the
 * `style` prop. Consumers must ensure a positioned ancestor exists.
 *
 * Usage:
 *   <SceneDialog tabLabel="Headmaster">
 *     Card content here
 *     <SceneDialog.Footer>
 *       <SceneDialog.Button>Let's go!</SceneDialog.Button>
 *     </SceneDialog.Footer>
 *   </SceneDialog>
 *
 * The Footer slot can hold any children — buttons, links, custom components.
 * They render in a row anchored to and overlapping the card's bottom edge.
 * Only one Footer is supported; extras are ignored (with a dev-mode warning).
 * Without a Footer, no button row is rendered.
 */

type SceneDialogSize = "default" | "wide";

type SceneDialogProps = {
	children: React.ReactNode;
	size?: SceneDialogSize;
	/** Optional label shown in a tab attached to the top-left of the card */
	tabLabel?: string;
	/** Accessible label for the callout. Use when there is no visible heading inside the card. */
	"aria-label"?: string;
	/** Positioning and any other wrapper styles — the component does not impose its own placement. */
	style?: React.CSSProperties;
	className?: string;
};

type ButtonProps = {
	children: React.ReactNode;
	variant?: "primary" | "secondary";
	onClick?: () => void;
	disabled?: boolean;
};

type FooterProps = {children: React.ReactNode};

type ClassValue = string | false | null | undefined;
const cx = (...classes: ClassValue[]): string =>
	classes.filter(Boolean).join(" ");

// Flatten transparent fragment wrappers so callers can group children in <>...</>
// without breaking slot detection. React.Children.toArray preserves fragments
// as elements rather than unwrapping them.
function flattenFragments(nodes: React.ReactNode): React.ReactNode[] {
	return React.Children.toArray(nodes).flatMap(child =>
		React.isValidElement(child) && child.type === React.Fragment
			? flattenFragments((child.props as {children: React.ReactNode}).children)
			: [child]
	);
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({children, variant = "primary", onClick, disabled = false}, ref) => (
		<button
			ref={ref}
			type="button"
			className={cx(
				styles.button,
				variant === "primary" ? styles.buttonPrimary : styles.buttonSecondary
			)}
			onClick={onClick}
			disabled={disabled}
			aria-disabled={disabled || undefined}
		>
			<span
				className={
					variant === "primary"
						? styles.buttonLabel
						: styles.buttonLabelSecondary
				}
			>
				{children}
			</span>
		</button>
	)
);
Button.displayName = "SceneDialog.Button";

// Footer is a marker slot: it renders nothing on its own. SceneDialog
// extracts its children and lays them out in the bottom button row.
// Identification is reference-based (child.type === Footer) — no string
// matching on displayName, so it survives minification, module reloads,
// and any consumer-side component memoization.
const Footer = (_props: FooterProps): null => null;
Footer.displayName = "SceneDialog.Footer";

function partitionFooter(
	children: React.ReactNode
): {
	body: React.ReactNode[];
	footer: React.ReactNode | null;
} {
	const body: React.ReactNode[] = [];
	let footer: React.ReactNode | null = null;
	let extraFooterCount = 0;
	for (const child of flattenFragments(children)) {
		if (React.isValidElement(child) && child.type === Footer) {
			if (footer === null) {
				footer = (child.props as FooterProps).children;
			} else {
				extraFooterCount += 1;
			}
		} else {
			body.push(child);
		}
	}
	if (extraFooterCount > 0 && process.env.NODE_ENV !== "production") {
		// eslint-disable-next-line no-console
		console.warn(
			`SceneDialog: ${extraFooterCount +
				1} <SceneDialog.Footer> elements found; only the first is rendered.`
		);
	}
	return {body, footer};
}

const SceneDialogInner = ({
	children,
	size = "default",
	tabLabel,
	"aria-label": ariaLabel,
	style,
	className,
}: SceneDialogProps) => {
	const labelId = React.useId();
	const {body, footer} = partitionFooter(children);
	const hasFooter = footer !== null;

	// Prefer explicit aria-label; fall back to labelling via the tab text.
	const labelProps: React.HTMLAttributes<HTMLDivElement> = {};
	if (ariaLabel) {
		labelProps["aria-label"] = ariaLabel;
	} else if (tabLabel) {
		labelProps["aria-labelledby"] = labelId;
	}

	return (
		<div
			className={cx(
				styles.dialogWrapper,
				size === "wide" && styles.dialogWrapperWide,
				className
			)}
			style={style}
		>
			<div
				role="region"
				{...labelProps}
				className={cx(styles.dialog, hasFooter && styles.dialogWithButtons)}
			>
				{tabLabel && (
					<div className={styles.tab}>
						<span id={labelId} className={styles.tabLabel}>
							{tabLabel}
						</span>
					</div>
				)}
				<div className={styles.dialogCopy}>{body}</div>
			</div>
			{/* buttonRow is a sibling of .dialog (not a child), absolutely
			    positioned against the wrapper, so the card's box-shadow stays
			    scoped to the card (never shadowing the buttons) and an open
			    dropdown isn't covered by the buttons. See the box-shadow
			    rationale in SceneDialog.module.css. */}
			{hasFooter && (
				<div className={styles.buttonRow}>
					<div className={styles.buttonRowInner}>{footer}</div>
				</div>
			)}
		</div>
	);
};
SceneDialogInner.displayName = "SceneDialog";

type SceneDialogCompound = typeof SceneDialogInner & {
	Button: typeof Button;
	Footer: typeof Footer;
};

export const SceneDialog: SceneDialogCompound = Object.assign(
	SceneDialogInner,
	{Button, Footer}
);

export {styles as sceneDialogStyles};
